import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { quietErrorsContext } from '../_helpers/error-support.interceptor';
import { environment } from '../../environments/environment';
import { BaseService } from './base/base.service';
import { SessionUserHashManagerService } from './session-user-hash-manager/session-user-hash-manager.service';
import { ClientReportFilter } from '../_models/reports/client-report-filter.model';
import { ClientReportBatchRequest } from '../_models/reports/client-report-batch-request.model';
import { BrandInfo } from '../_models/reports/brand-info.model';
import { AttendanceReportRow } from '../_models/reports/attendance-report-row.model';
import { CohortProgressRow } from '../_models/reports/cohort-progress-row.model';
import { ProgressOverviewRow } from '../_models/reports/progress-overview-row.model';
import { MilprReport } from '../_models/reports/milpr-report.model';

/**
 * Typed calls to the native client-report endpoints (api/ClientReport).
 *
 * Follows the shared BaseService pattern (Bearer + RequestId + RequestMatch
 * headers via prepareOptions; status===299 / HttpErrorResponse guard; handleError).
 * prepareOptions() returns { headers, observe: 'response' }, so every response
 * arrives as an HttpResponse and must be unwrapped via `unwrap()` to yield the
 * actual payload.
 */
@Injectable()
export class ClientReportService extends BaseService {

    private apiUrl: string = environment.apiUrl;

    constructor(
        private http: HttpClient,
        protected hashManagerService: SessionUserHashManagerService
    ) {
        super(hashManagerService);
        this.apiUrl += this.apiUrl.endsWith('/') ? '' : '/';
    }

    /** Loads the brand list used to seed the report filter's company picker. */
    public getBrands(): Observable<BrandInfo[]> {
        return this.http.get(this.apiUrl + 'api/ClientReport/brands', this.prepareOptions(null)).pipe(
            map((data: any) => this.unwrap(data) as BrandInfo[]),
            catchError(this.handleError('getBrands'))
        );
    }

    /** Runs the attendance report for the given filter. */
    public runAttendance(filter: ClientReportFilter): Observable<AttendanceReportRow[]> {
        return this.runReport<AttendanceReportRow[]>('attendance', filter);
    }

    /** Runs the MILPR report for the given filter. */
    public runMilpr(filter: ClientReportFilter): Observable<MilprReport> {
        return this.runReport<MilprReport>('milpr', filter);
    }

    /** Runs the cohort progress report for the given filter. */
    public runCohortProgress(filter: ClientReportFilter): Observable<CohortProgressRow[]> {
        return this.runReport<CohortProgressRow[]>('cohortprogress', filter);
    }

    /** Runs the progress overview report for the given filter. */
    public runProgressOverview(filter: ClientReportFilter): Observable<ProgressOverviewRow[]> {
        return this.runReport<ProgressOverviewRow[]>('progressoverview', filter);
    }

    /**
     * Requests the given report rendered to an exported file (blob): xlsx, or a PDF with format 'pdf'
     * (404 while the reports.client-pdf switch is off).
     */
    public exportReport(reportCode: string, filter: ClientReportFilter, format: 'xlsx' | 'pdf' = 'xlsx'): Observable<Blob> {
        const options = { ...this.prepareOptions(filter), responseType: 'blob' as 'blob' };
        const query = format === 'pdf' ? '?format=pdf' : '';
        return this.http.post(this.apiUrl + 'api/ClientReport/' + reportCode + '/export' + query, filter, options).pipe(
            map((data: any) => this.unwrap(data) as Blob),
            catchError(this.handleError('exportReport'))
        );
    }

    /**
     * A client report as PDF. Resolves to the file, or, for a report large enough to be prepared in the background
     * (reports.pdf-background), to the job to poll with getPdfJob. A refusal (422, too large) or a render failure
     * (503) fails with the API's own message, which the Reports screen shows as it is. The failure is still logged,
     * but the support popup is not raised: the screen says what happened.
     */
    public exportPdf(reportCode: string, filter: ClientReportFilter): Observable<ClientReportPdfExport> {
        const options = { ...this.prepareOptions(filter), responseType: 'blob' as 'blob', context: quietErrorsContext() };
        return this.http.post(this.apiUrl + 'api/ClientReport/' + reportCode + '/export?format=pdf', filter, options).pipe(
            switchMap((response: any) => response?.status === 202
                ? from(blobText(response.body)).pipe(map(text => ({ job: JSON.parse(text) as ClientReportPdfJobState })))
                : of({ file: response.body as Blob })),
            catchError((error: any) => {
                if (error instanceof HttpErrorResponse && (error.status === 422 || error.status === 503) && error.error instanceof Blob) {
                    return from(blobText(error.error)).pipe(switchMap(text => throwError(() => ({ message: text, status: error.status }))));
                }
                return throwError(() => ({ message: PDF_FAILED_MESSAGE, status: error?.status }));
            })
        );
    }

    /** A background PDF's state (reports.pdf-background); only the person who asked for it can read it. */
    public getPdfJob(jobId: number): Observable<ClientReportPdfJobState> {
        return this.http.get(this.apiUrl + 'api/ClientReport/pdf-jobs/' + jobId,
            { ...this.prepareOptions(null), context: quietErrorsContext() }).pipe(
            map((data: any) => this.unwrap(data) as ClientReportPdfJobState)
        );
    }

    /** A generated report's file, through the API's access check (api/Files/Report/{id}). */
    public downloadGeneratedReport(generatedReportId: number): Observable<Blob> {
        const options = { ...this.prepareOptions(null), responseType: 'blob' as 'blob' };
        return this.http.get(this.apiUrl + 'api/Files/Report/' + generatedReportId, options).pipe(
            map((data: any) => data.body as Blob)
        );
    }

    /** Queues a batch of reports for background generation. Resolves to the new batch id. */
    public runBatch(request: ClientReportBatchRequest): Observable<number> {
        return this.http.post(this.apiUrl + 'api/ClientReport/batch', request, this.prepareOptions(request)).pipe(
            map((data: any) => this.unwrap(data) as number),
            catchError(this.handleError('runBatch'))
        );
    }

    private runReport<T>(reportCode: string, filter: ClientReportFilter): Observable<T> {
        return this.http.post(this.apiUrl + 'api/ClientReport/' + reportCode + '/run', filter, this.prepareOptions(filter)).pipe(
            map((data: any) => this.unwrap(data) as T),
            catchError(this.handleError('run:' + reportCode))
        );
    }

    /**
     * Shared body unwrap used by every method — 422 business-validation failures arrive on the
     * error channel and are mapped by BaseService.handleError.
     */
    private unwrap(data: any): any {
        return data ? data.body : data;
    }
}

/** A background client report PDF's state (GET api/ClientReport/pdf-jobs/{id}). */
export interface ClientReportPdfJobState {
    jobId: number;
    /** Queued, Running, Done or Failed. */
    status: string;
    /** Set when Done: the file downloads with downloadGeneratedReport. */
    generatedReportId?: number;
    /** Set when Failed: what to tell the person. */
    message?: string;
}

/** A PDF export's outcome: the file now, or a job preparing it in the background. */
export interface ClientReportPdfExport {
    file?: Blob;
    job?: ClientReportPdfJobState;
}

/** Shown when a PDF fails for a reason the API did not explain. */
export const PDF_FAILED_MESSAGE = 'The PDF could not be produced. Try again, or use Excel.';

function blobText(blob: Blob): Promise<string> {
    return blob ? blob.text() : Promise.resolve('');
}
